import { openDB, type DBSchema, type IDBPDatabase } from "idb";

/**
 * คิวรูปในเครื่อง (IndexedDB) ให้รูปอยู่รอดตอน redirect ไป login และตอนออฟไลน์
 * เก็บเป็น ArrayBuffer + type เพื่อให้ใช้ได้กับทุกเบราว์เซอร์ (iOS Safari เก่ามีปัญหากับ Blob)
 * ไม่เก็บชื่อไฟล์เดิมหรือ metadata ส่วนบุคคล
 */
export interface StoredPhoto {
  id: string;
  buffer: ArrayBuffer;
  type: string;
  width: number;
  height: number;
  createdAt: number;
  /** ถ่ายตอนออฟไลน์ รอส่ง */
  pending: boolean;
}

export interface PhotoView extends Omit<StoredPhoto, "buffer"> {
  blob: Blob;
}

interface Schema extends DBSchema {
  photos: { key: string; value: StoredPhoto; indexes: { byCreated: number } };
  kv: { key: string; value: unknown };
}

const DB_NAME = "workcost";
const DB_VERSION = 1;
export const MAX_STORED_PHOTOS = 3;

let dbPromise: Promise<IDBPDatabase<Schema>> | null = null;

function db(): Promise<IDBPDatabase<Schema>> {
  dbPromise ??= openDB<Schema>(DB_NAME, DB_VERSION, {
    upgrade(d) {
      const photos = d.createObjectStore("photos", { keyPath: "id" });
      photos.createIndex("byCreated", "createdAt");
      d.createObjectStore("kv");
    },
  });
  return dbPromise;
}

/** สำหรับทดสอบ: ปิดและลืมการเชื่อมต่อ */
export async function resetPhotoStoreForTests(): Promise<void> {
  if (dbPromise) (await dbPromise).close();
  dbPromise = null;
}

function newId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

async function toBuffer(blob: Blob): Promise<ArrayBuffer> {
  return typeof blob.arrayBuffer === "function" ? blob.arrayBuffer() : new Response(blob).arrayBuffer();
}

function toView(p: StoredPhoto): PhotoView {
  const { buffer, ...rest } = p;
  return { ...rest, blob: new Blob([buffer], { type: p.type }) };
}

export class PhotoLimitError extends Error {
  constructor() {
    super("photo_limit");
    this.name = "PhotoLimitError";
  }
}

export async function addPhoto(input: { blob: Blob; width: number; height: number; pending?: boolean }): Promise<PhotoView> {
  // อ่าน buffer ให้เสร็จก่อนเปิด transaction เพราะ await งานนอก IndexedDB ระหว่าง transaction ทำให้ transaction ปิดเอง
  const buffer = await toBuffer(input.blob);
  const d = await db();
  const tx = d.transaction("photos", "readwrite");
  if ((await tx.store.count()) >= MAX_STORED_PHOTOS) {
    await tx.done;
    throw new PhotoLimitError();
  }
  const rec: StoredPhoto = {
    id: newId(),
    buffer,
    type: input.blob.type || "image/jpeg",
    width: input.width,
    height: input.height,
    createdAt: Date.now(),
    pending: input.pending ?? false,
  };
  await tx.store.put(rec);
  await tx.done;
  return toView(rec);
}

/** ใส่รูปที่ลบไปกลับเข้าที่เดิม (undo) */
export async function restorePhoto(photo: PhotoView): Promise<void> {
  const d = await db();
  const { blob, ...rest } = photo;
  await d.put("photos", { ...rest, buffer: await toBuffer(blob) });
}

export async function listPhotos(): Promise<PhotoView[]> {
  const d = await db();
  const all = await d.getAllFromIndex("photos", "byCreated");
  return all.map(toView);
}

export async function removePhoto(id: string): Promise<void> {
  await (await db()).delete("photos", id);
}

export async function clearPhotos(): Promise<void> {
  await (await db()).clear("photos");
}

/** เคลียร์สถานะรอส่งเมื่อกลับมาออนไลน์ */
export async function markAllSent(): Promise<void> {
  const d = await db();
  const tx = d.transaction("photos", "readwrite");
  for (const p of await tx.store.getAll()) if (p.pending) await tx.store.put({ ...p, pending: false });
  await tx.done;
}

export async function getKv<T>(key: string): Promise<T | undefined> {
  return (await (await db()).get("kv", key)) as T | undefined;
}

export async function setKv(key: string, value: unknown): Promise<void> {
  await (await db()).put("kv", value, key);
}

/** ล้างข้อมูลในเครื่องทั้งหมดของส่วนนี้ (รูปและค่าที่เก็บไว้) ใช้ตอนลบบัญชี */
export async function clearAllLocal(): Promise<void> {
  const d = await db();
  const tx = d.transaction(["photos", "kv"], "readwrite");
  await Promise.all([tx.objectStore("photos").clear(), tx.objectStore("kv").clear(), tx.done]);
}
